/**
 * Contact Service - Static version (Firebase removed)
 * Form validation runs client-side; submissions log to console only.
 */

/**
 * Contact form validation rules
 */
const ValidationRules = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s'-]+$/
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  },
  subject: {
    required: true,
    validOptions: ['general', 'summit', 'partnership', 'programs', 'funding', 'media', 'careers']
  },
  message: {
    required: true,
    minLength: 10,
    maxLength: 1000
  }
};

/**
 * Validate form data
 */
export const validateContactForm = (formData) => {
  const errors = {};

  // Validate name
  if (!formData.name?.trim()) {
    errors.name = 'Name is required';
  } else if (formData.name.trim().length < ValidationRules.name.minLength) {
    errors.name = `Name must be at least ${ValidationRules.name.minLength} characters`;
  } else if (formData.name.trim().length > ValidationRules.name.maxLength) {
    errors.name = `Name must be less than ${ValidationRules.name.maxLength} characters`;
  } else if (!ValidationRules.name.pattern.test(formData.name.trim())) {
    errors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes';
  }

  // Validate email
  if (!formData.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!ValidationRules.email.pattern.test(formData.email.trim())) {
    errors.email = 'Please enter a valid email address';
  }

  // Validate subject
  if (!formData.subject) {
    errors.subject = 'Please select a subject';
  } else if (!ValidationRules.subject.validOptions.includes(formData.subject)) {
    errors.subject = 'Please select a valid subject';
  }

  // Validate message
  if (!formData.message?.trim()) {
    errors.message = 'Message is required';
  } else if (formData.message.trim().length < ValidationRules.message.minLength) {
    errors.message = `Message must be at least ${ValidationRules.message.minLength} characters`;
  } else if (formData.message.trim().length > ValidationRules.message.maxLength) {
    errors.message = `Message must be less than ${ValidationRules.message.maxLength} characters`;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

const GOOGLE_SHEETS_WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbxoLQISXNm67llf5maHGltOUM3givmru_rKkpb_vI1ktYAk3Zk4CDRyS2Ug7qNm3NfAsg/exec';

/**
 * Sanitize form data
 */
const sanitizeFormData = (formData) => {
  return {
    name: formData.name?.trim() || '',
    email: formData.email?.trim().toLowerCase() || '',
    subject: formData.subject || '',
    message: formData.message?.trim() || ''
  };
};

/**
 * Submit contact form - transmits to Google Sheets webhook
 */
export const submitContactForm = async (formData) => {
  try {
    const sanitizedData = sanitizeFormData(formData);

    const validation = validateContactForm(sanitizedData);
    if (!validation.isValid) {
      return {
        success: false,
        errors: validation.errors,
        message: 'Please correct the errors in your form and try again.'
      };
    }

    // Send to Google Sheets Webhook
    if (GOOGLE_SHEETS_WEBHOOK_URL) {
      try {
        await fetch(GOOGLE_SHEETS_WEBHOOK_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'text/plain;charset=utf-8'
          },
          body: JSON.stringify({
            formType: 'contact',
            ...sanitizedData,
            timestamp: new Date().toISOString()
          })
        });
        console.info('Contact message sent to Google Sheets');
      } catch (sheetErr) {
        console.warn('Google Sheets contact webhook error:', sheetErr);
      }
    }

    return {
      success: true,
      message: "Thank you for reaching out! We've received your message and will get back to you within 24 hours."
    };
  } catch (error) {
    console.error('Contact form submission error:', error);
    return {
      success: false,
      message: 'Sorry, something went wrong. Please try again or contact us directly at info@yocilab.org'
    };
  }
};

/**
 * Get subject label for display
 */
export const getSubjectLabel = (subjectValue) => {
  const subjects = {
    general: 'General Inquiry',
    summit: 'Summit 2026 Registration / Inquiries',
    partnership: 'Partnership Opportunity',
    programs: 'Programs & Services',
    funding: 'Funding & Investment',
    media: 'Media & Press',
    careers: 'Careers & Opportunities'
  };
  return subjects[subjectValue] || subjectValue;
};

/**
 * Format timestamp for display
 */
export const formatSubmissionDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(date);
};

/**
 * Export validation rules for use in components
 */
export { ValidationRules };